import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { api } from '@/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { isProfessionalRole } from '@/utils/roles';
import {
  Building2, FileText, Clock, CheckCircle2, XCircle, AlertTriangle,
  Calendar, Loader2, RefreshCw, ChevronRight, Eye, File, Download,
  MessageSquare, AlertCircle, Send, ClipboardList, FileQuestion,
  Briefcase, Bell, Home, LogOut, User, Settings, ArrowRight, ExternalLink
} from 'lucide-react';

const STATUS_COLORS = {
  'Pending': 'bg-amber-100 text-amber-700',
  'Under Review': 'bg-blue-100 text-blue-700',
  'Responded': 'bg-green-100 text-green-700',
  'Approved': 'bg-green-100 text-green-700',
  'Rejected': 'bg-red-100 text-red-700',
  'Overdue': 'bg-red-100 text-red-700'
};

const ConsultantPortal = () => {
  const navigate = useNavigate();
  const { user: authUser, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Data states
  const [pendingItems, setPendingItems] = useState({
    notices: [],
    claims: [],
    rfis: [],
    shopDrawings: [],
    delayEvents: []
  });
  const [stats, setStats] = useState({});
  
  // Selected items for inline detail expansion
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [selectedRfi, setSelectedRfi] = useState(null);
  const [selectedShopDrawing, setSelectedShopDrawing] = useState(null);
  const [selectedDelayEvent, setSelectedDelayEvent] = useState(null);
  const [selectedSI, setSelectedSI] = useState(null);
  
  // Response dialog
  const [responseDialog, setResponseDialog] = useState({ open: false, type: null, item: null });
  const [responseForm, setResponseForm] = useState({
    decision: '',
    comments: '',
    attachments: []
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Site Instructions state
  const [siteInstructions, setSiteInstructions] = useState([]);
  const [siDialogOpen, setSiDialogOpen] = useState(false);
  const [siForm, setSiForm] = useState({
    project_id: '',
    si_title: '',
    si_description: '',
    discipline: 'General',
    location_area: '',
    issued_to: 'Main Contractor',
    cost_impact_expected: 'Unknown',
    time_impact_expected: 'Unknown',
    priority: 'normal',
    drawings_referenced: '',
    affected_work_packages: ''
  });
  const [nextSiNumber, setNextSiNumber] = useState('');
  const [siSubmitting, setSiSubmitting] = useState(false);

  // Check authentication using AuthContext
  useEffect(() => {
    if (authLoading) return; // Wait for auth to load
    
    if (!authUser) {
      navigate('/login?redirect=/consultant-portal');
      return;
    }
    
    if (!isProfessionalRole(authUser.role) && authUser.role !== 'admin' && authUser.role !== 'contracts_manager') {
      toast.error('Access denied. Consultant, PM, or PA role required.');
      navigate('/');
      return;
    }
  }, [authUser, authLoading, navigate]);

  const loadData = useCallback(async () => {
    if (!authUser || authLoading) return;
    
    setLoading(true);
    try {
      // Load projects where user is a consultant/PA
      const projectsRes = await api.get('/consultant/projects');
      setProjects(projectsRes.data.projects || []);
      
      // Load pending items across all projects
      const pendingRes = await api.get('/consultant/pending-items');
      setPendingItems(pendingRes.data);
      
      // Load stats
      const statsRes = await api.get('/consultant/stats');
      setStats(statsRes.data);
      
      // Load site instructions issued by this consultant
      const siRes = await api.get('/consultant/site-instructions');
      setSiteInstructions(siRes.data.site_instructions || []);
      
    } catch (err) {
      console.error('Load error:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [authUser, authLoading]);

  useEffect(() => {
    if (authUser && !authLoading) {
      loadData();
    }
  }, [authUser, authLoading, loadData]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const openResponseDialog = (type, item) => {
    setResponseDialog({ open: true, type, item });
    setResponseForm({ decision: '', comments: '', attachments: [] });
  };

  const handleSubmitResponse = async () => {
    if (!responseForm.decision) {
      toast.error('Please select a decision');
      return;
    }

    setSubmitting(true);
    try {
      const { type, item } = responseDialog;
      let endpoint = '';
      let payload = {
        decision: responseForm.decision,
        comments: responseForm.comments
      };
      
      switch (type) {
        case 'notice':
          endpoint = `/consultant/notices/${item.id}/respond`;
          break;
        case 'claim':
          endpoint = `/consultant/claims/${item.id}/respond`;
          break;
        case 'rfi':
          endpoint = `/consultant/rfis/${item.id}/respond`;
          break;
        case 'shopDrawing':
          endpoint = `/shop-drawings/cycles/${item.cycle_id}/consultant-response`;
          break;
        case 'delayEvent':
          endpoint = `/consultant/delay-events/${item.id}/respond`;
          payload = {
            response_type: responseForm.decision === 'determination' ? 'determination' : 'comment',
            comments: responseForm.comments,
            recommendation: responseForm.decision !== 'comment' ? responseForm.decision : null
          };
          break;
        default:
          throw new Error('Invalid type');
      }

      await api.post(endpoint, payload);

      toast.success('Response submitted successfully');
      setResponseDialog({ open: false, type: null, item: null });
      loadData();
    } catch (err) {
      toast.error('Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const calculateDaysOverdue = (deadline) => {
    if (!deadline) return 0;
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = Math.ceil((now - deadlineDate) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  // Site Instruction functions
  const openSiDialog = async (projectId = '') => {
    setSiForm({
      project_id: projectId || (projects.length > 0 ? projects[0].id : ''),
      si_title: '',
      si_description: '',
      discipline: 'General',
      location_area: '',
      issued_to: 'Main Contractor',
      cost_impact_expected: 'Unknown',
      time_impact_expected: 'Unknown',
      priority: 'normal',
      drawings_referenced: '',
      affected_work_packages: ''
    });
    
    // Get next SI number for the selected project
    const targetProjectId = projectId || (projects.length > 0 ? projects[0].id : '');
    if (targetProjectId) {
      try {
        const res = await api.get(`/consultant/site-instructions/next-number/${targetProjectId}`);
        setNextSiNumber(res.data.next_si_number);
      } catch (err) {
        setNextSiNumber('SI-001');
      }
    }
    
    setSiDialogOpen(true);
  };

  const handleProjectChange = async (projectId) => {
    setSiForm(prev => ({ ...prev, project_id: projectId }));
    if (projectId) {
      try {
        const res = await api.get(`/consultant/site-instructions/next-number/${projectId}`);
        setNextSiNumber(res.data.next_si_number);
      } catch (err) {
        setNextSiNumber('SI-001');
      }
    }
  };

  const handleSiSubmit = async () => {
    if (!siForm.project_id) {
      toast.error('Please select a project');
      return;
    }
    if (!siForm.si_title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!siForm.si_description.trim()) {
      toast.error('Please enter a description');
      return;
    }
    
    setSiSubmitting(true);
    try {
      const payload = {
        ...siForm,
        drawings_referenced: siForm.drawings_referenced ? siForm.drawings_referenced.split(',').map(d => d.trim()).filter(Boolean) : [],
        affected_work_packages: siForm.affected_work_packages ? siForm.affected_work_packages.split(',').map(w => w.trim()).filter(Boolean) : []
      };
      
      const res = await api.post('/consultant/site-instructions', payload);
      
      toast.success(`Site Instruction ${res.data.site_instruction.si_number} created successfully!`);
      
      if (res.data.email_sent) {
        toast.success('Email notification sent to contractor');
      }
      
      setSiDialogOpen(false);
      loadData(); // Refresh data
      
    } catch (err) {
      console.error('SI creation error:', err);
      toast.error(err.response?.data?.detail || 'Failed to create Site Instruction');
    } finally {
      setSiSubmitting(false);
    }
  };

  // Show loading while auth is loading or data is loading
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-slate-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading portal...</p>
        </div>
      </div>
    );
  }

  // If no user after loading, redirect handled in useEffect
  if (!authUser) {
    return null;
  }

  const getItemUrl = (item) => {
    switch (item.type) {
      case 'notice': return `/notices/${item.id}`;
      case 'claim': return `/claims/${item.id}`;
      case 'rfi': return `/projects/${item.project_id}/rfi/${item.id}`;
      case 'shop_drawing': return `/shop-drawings/${item.id}`;
      case 'delay_event': return `/events/${item.id}`;
      default: return item.project_id ? `/projects/${item.project_id}` : '/dashboard';
    }
  };

  const totalPending = (pendingItems.notices?.length || 0) + 
                       (pendingItems.claims?.length || 0) + 
                       (pendingItems.rfis?.length || 0) + 
                       (pendingItems.shopDrawings?.length || 0);

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Briefcase className="h-6 w-6 sm:h-8 sm:w-8 text-slate-400 shrink-0" />
              <div className="min-w-0">
                <h1 className="text-sm sm:text-lg font-bold tracking-wide truncate">CONSULTANT PORTAL</h1>
                <p className="text-[10px] sm:text-xs text-slate-400 hidden sm:block">EOT Claims Hub</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 shrink-0">
              {totalPending > 0 && (
                <Badge className="bg-red-600 text-white px-2 sm:px-3 py-0.5 sm:py-1 text-xs">
                  {totalPending} <span className="hidden sm:inline">Pending</span>
                </Badge>
              )}
              <div className="hidden sm:flex items-center gap-2 text-sm">
                <User className="h-4 w-4" />
                <span className="truncate max-w-[120px]">{authUser?.full_name || authUser?.name || authUser?.email}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-300 hover:text-white px-2">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <Card className="bg-white border-l-4 border-l-amber-500">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-500">Pending Notices</p>
                  <p className="text-2xl sm:text-3xl font-bold text-slate-900">{pendingItems.notices?.length || 0}</p>
                </div>
                <AlertTriangle className="h-6 w-6 sm:h-8 sm:w-8 text-amber-500 shrink-0" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-l-4 border-l-purple-500">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-500">Pending Claims</p>
                  <p className="text-2xl sm:text-3xl font-bold text-slate-900">{pendingItems.claims?.length || 0}</p>
                </div>
                <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-purple-500 shrink-0" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-l-4 border-l-blue-500">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-500">Pending RFIs</p>
                  <p className="text-2xl sm:text-3xl font-bold text-slate-900">{pendingItems.rfis?.length || 0}</p>
                </div>
                <FileQuestion className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500 shrink-0" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-l-4 border-l-indigo-500">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-500">Shop Drawings</p>
                  <p className="text-2xl sm:text-3xl font-bold text-slate-900">{pendingItems.shopDrawings?.length || 0}</p>
                </div>
                <ClipboardList className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-500 shrink-0" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
            <TabsList className="bg-white border inline-flex w-auto min-w-full sm:min-w-0">
              <TabsTrigger value="dashboard" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3">
                <Home className="h-4 w-4 sm:mr-2 shrink-0" />
                <span className="hidden sm:inline">Dashboard</span>
              </TabsTrigger>
              <TabsTrigger value="notices" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3">
                <AlertTriangle className="h-4 w-4 sm:mr-2 shrink-0" />
                <span className="hidden sm:inline">Notices</span> <span className="sm:hidden">{pendingItems.notices?.length || 0}</span><span className="hidden sm:inline"> ({pendingItems.notices?.length || 0})</span>
              </TabsTrigger>
              <TabsTrigger value="claims" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3">
                <FileText className="h-4 w-4 sm:mr-2 shrink-0" />
                <span className="hidden sm:inline">Claims</span> <span className="sm:hidden">{pendingItems.claims?.length || 0}</span><span className="hidden sm:inline"> ({pendingItems.claims?.length || 0})</span>
              </TabsTrigger>
              <TabsTrigger value="rfis" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3">
                <FileQuestion className="h-4 w-4 sm:mr-2 shrink-0" />
                <span className="hidden sm:inline">RFIs</span> <span className="sm:hidden">{pendingItems.rfis?.length || 0}</span><span className="hidden sm:inline"> ({pendingItems.rfis?.length || 0})</span>
              </TabsTrigger>
              <TabsTrigger value="delayEvents" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3">
                <AlertTriangle className="h-4 w-4 sm:mr-2 shrink-0" />
                <span className="hidden sm:inline">Delay Events</span> <span className="sm:hidden">{pendingItems.delayEvents?.length || 0}</span><span className="hidden sm:inline"> ({pendingItems.delayEvents?.length || 0})</span>
              </TabsTrigger>
              <TabsTrigger value="shopDrawings" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3">
                <ClipboardList className="h-4 w-4 sm:mr-2 shrink-0" />
                <span className="hidden sm:inline">Shop Drawings</span> <span className="sm:hidden">{pendingItems.shopDrawings?.length || 0}</span><span className="hidden sm:inline"> ({pendingItems.shopDrawings?.length || 0})</span>
              </TabsTrigger>
              <TabsTrigger value="siteInstructions" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3">
                <Send className="h-4 w-4 sm:mr-2 shrink-0" />
                <span className="hidden sm:inline">Site Instructions</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Urgent Items */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    Urgent / Overdue Items
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {[...pendingItems.notices, ...pendingItems.claims]
                    .filter(item => calculateDaysOverdue(item.response_deadline) > 0)
                    .slice(0, 5)
                    .map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 border-b last:border-0 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(getItemUrl(item))} data-testid={`urgent-item-${idx}`}>
                        <div>
                          <p className="font-medium text-sm">{item.title || item.reference}</p>
                          <p className="text-xs text-slate-500">{item.project_name}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-red-100 text-red-700">
                            {calculateDaysOverdue(item.response_deadline)} days overdue
                          </Badge>
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        </div>
                      </div>
                    )) || (
                    <p className="text-sm text-slate-500 text-center py-8">No urgent items</p>
                  )}
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                    <Clock className="h-4 w-4 text-slate-500" />
                    Recent Submissions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {[...pendingItems.notices, ...pendingItems.claims, ...pendingItems.rfis]
                    .sort((a, b) => new Date(b.submitted_at || b.created_at) - new Date(a.submitted_at || a.created_at))
                    .slice(0, 5)
                    .map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 border-b last:border-0 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(getItemUrl(item))} data-testid={`recent-item-${idx}`}>
                        <div>
                          <p className="font-medium text-sm">{item.title || item.reference}</p>
                          <p className="text-xs text-slate-500">{formatDate(item.submitted_at || item.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{item.type || 'Item'}</Badge>
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        </div>
                      </div>
                    )) || (
                    <p className="text-sm text-slate-500 text-center py-8">No recent items</p>
                  )}
                </CardContent>
              </Card>

              {/* Projects */}
              <Card className="lg:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-slate-500" />
                    My Projects ({projects.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <p className="col-span-full text-xs sm:text-sm text-slate-500 -mb-2">Select a project below to view and manage notices, RFIs, and other module details.</p>
                    {projects.map(project => (
                      <div 
                        key={project.id} 
                        className="p-4 border rounded-lg hover:border-slate-400 cursor-pointer transition-colors"
                        onClick={() => setSelectedProject(project)}
                      >
                        <h3 className="font-bold text-slate-900">{project.name}</h3>
                        <p className="text-sm text-slate-500 mb-2">{project.client_name}</p>
                        <div className="flex gap-2">
                          <Badge variant="outline" className="text-xs">
                            {project.pending_count || 0} pending
                          </Badge>
                        </div>
                      </div>
                    ))}
                    {projects.length === 0 && (
                      <p className="text-sm text-slate-500 col-span-full text-center py-8">
                        No projects assigned
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Notices Tab */}
          <TabsContent value="notices">
            <Card>
              <CardContent className="p-0">
                {pendingItems.notices?.length > 0 ? (
                  <div className="divide-y">
                    {pendingItems.notices.map(notice => (
                      <div 
                        key={notice.id} 
                        className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${selectedNotice?.id === notice.id ? 'bg-slate-50 border-l-4 border-slate-900' : ''}`}
                        onClick={() => setSelectedNotice(selectedNotice?.id === notice.id ? null : notice)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-bold text-slate-900">{notice.reference}</h3>
                              <Badge className={STATUS_COLORS[notice.status] || 'bg-slate-100'}>
                                {notice.status}
                              </Badge>
                              {calculateDaysOverdue(notice.response_deadline) > 0 && (
                                <Badge className="bg-red-100 text-red-700">
                                  {calculateDaysOverdue(notice.response_deadline)} days overdue
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mb-2">{notice.event_type} - {notice.event_narrative?.substring(0, 100)}...</p>
                            <div className="flex items-center gap-2 sm:gap-4 text-xs text-slate-500 flex-wrap flex-wrap">
                              <span><Building2 className="h-3 w-3 inline mr-1" />{notice.project_name}</span>
                              <span><Calendar className="h-3 w-3 inline mr-1" />Submitted: {formatDate(notice.submitted_at)}</span>
                              <span className="hidden sm:inline"><Clock className="h-3 w-3 inline mr-1" />Response due: {formatDate(notice.response_deadline)}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-2 sm:ml-4 shrink-0">
                            <ArrowRight className={`h-5 w-5 text-slate-400 transition-transform ${selectedNotice?.id === notice.id ? 'rotate-90' : ''}`} />
                          </div>
                        </div>
                        
                        {/* Expanded Detail */}
                        {selectedNotice?.id === notice.id && (
                          <div className="mt-4 pt-4 border-t space-y-3" onClick={(e) => e.stopPropagation()}>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-sm">
                              <div>
                                <p className="text-slate-500">Contract Clause</p>
                                <p className="font-medium">{notice.contract_clause || '-'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Days Claimed</p>
                                <p className="font-medium">{notice.days_claimed || '-'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Cost Claimed</p>
                                <p className="font-medium">{notice.cost_claimed ? `R ${notice.cost_claimed.toLocaleString()}` : '-'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Response Deadline</p>
                                <p className="font-medium">{formatDate(notice.response_deadline)}</p>
                              </div>
                            </div>
                            {notice.event_narrative && (
                              <div>
                                <p className="text-slate-500 text-sm font-medium">Event Narrative</p>
                                <p className="text-sm whitespace-pre-line">{notice.event_narrative}</p>
                              </div>
                            )}
                            <div className="flex flex-col sm:flex-row gap-2 pt-2">
                              <Button 
                                size="sm" 
                                className="bg-slate-900 hover:bg-slate-800"
                                onClick={() => openResponseDialog('notice', notice)}
                              >
                                <Send className="h-4 w-4 mr-1" />
                                Respond to Notice
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => navigate(`/notices/${notice.id}`)}>
                                <ExternalLink className="h-4 w-4 mr-1" />
                                View Full Details
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                    <p className="text-slate-600">No pending notices</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Claims Tab */}
          <TabsContent value="claims">
            <Card>
              <CardContent className="p-0">
                {pendingItems.claims?.length > 0 ? (
                  <div className="divide-y">
                    {pendingItems.claims.map(claim => (
                      <div 
                        key={claim.id} 
                        className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${selectedClaim?.id === claim.id ? 'bg-slate-50 border-l-4 border-slate-900' : ''}`}
                        onClick={() => setSelectedClaim(selectedClaim?.id === claim.id ? null : claim)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-bold text-slate-900">{claim.reference}</h3>
                              <Badge className={STATUS_COLORS[claim.status] || 'bg-slate-100'}>
                                {claim.status}
                              </Badge>
                              {claim.days_claimed && (
                                <Badge variant="outline">{claim.days_claimed} days claimed</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mb-2">{claim.event_type} - {claim.narrative?.substring(0, 100)}...</p>
                            <div className="flex items-center gap-2 sm:gap-4 text-xs text-slate-500 flex-wrap">
                              <span><Building2 className="h-3 w-3 inline mr-1" />{claim.project_name}</span>
                              <span><Calendar className="h-3 w-3 inline mr-1" />Submitted: {formatDate(claim.submitted_at)}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-2 sm:ml-4 shrink-0">
                            <ArrowRight className={`h-5 w-5 text-slate-400 transition-transform ${selectedClaim?.id === claim.id ? 'rotate-90' : ''}`} />
                          </div>
                        </div>
                        
                        {/* Expanded Detail */}
                        {selectedClaim?.id === claim.id && (
                          <div className="mt-4 pt-4 border-t space-y-3" onClick={(e) => e.stopPropagation()}>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-sm">
                              <div>
                                <p className="text-slate-500">Days Claimed</p>
                                <p className="font-medium">{claim.days_claimed || '-'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Cost Claimed</p>
                                <p className="font-medium">{claim.cost_claimed ? `R ${claim.cost_claimed.toLocaleString()}` : '-'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Contract Clause</p>
                                <p className="font-medium">{claim.contract_clause || '-'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Event Type</p>
                                <p className="font-medium">{claim.event_type || '-'}</p>
                              </div>
                            </div>
                            {claim.narrative && (
                              <div>
                                <p className="text-slate-500 text-sm font-medium">Claim Narrative</p>
                                <p className="text-sm whitespace-pre-line">{claim.narrative}</p>
                              </div>
                            )}
                            <div className="flex flex-col sm:flex-row gap-2 pt-2">
                              <Button 
                                size="sm" 
                                className="bg-slate-900 hover:bg-slate-800"
                                onClick={() => openResponseDialog('claim', claim)}
                              >
                                <Send className="h-4 w-4 mr-1" />
                                Adjudicate Claim
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => navigate(`/claims/${claim.id}`)}>
                                <ExternalLink className="h-4 w-4 mr-1" />
                                View Full Details
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                    <p className="text-slate-600">No pending claims</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* RFIs Tab */}
          <TabsContent value="rfis">
            <Card>
              <CardContent className="p-0">
                {pendingItems.rfis?.length > 0 ? (
                  <div className="divide-y">
                    {pendingItems.rfis.map(rfi => (
                      <div 
                        key={rfi.id} 
                        className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${selectedRfi?.id === rfi.id ? 'bg-slate-50 border-l-4 border-slate-900' : ''}`}
                        onClick={() => setSelectedRfi(selectedRfi?.id === rfi.id ? null : rfi)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-bold text-slate-900">{rfi.rfi_number}</h3>
                              <Badge className={STATUS_COLORS[rfi.status] || 'bg-slate-100'}>
                                {rfi.status}
                              </Badge>
                              <Badge variant="outline">{rfi.discipline}</Badge>
                            </div>
                            <p className="text-sm text-slate-600 mb-2">{rfi.title || rfi.query?.substring(0, 100)}...</p>
                            <div className="flex items-center gap-2 sm:gap-4 text-xs text-slate-500 flex-wrap">
                              <span><Building2 className="h-3 w-3 inline mr-1" />{rfi.project_name}</span>
                              <span><Calendar className="h-3 w-3 inline mr-1" />Due: {formatDate(rfi.response_due_date)}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-2 sm:ml-4 shrink-0">
                            <ArrowRight className={`h-5 w-5 text-slate-400 transition-transform ${selectedRfi?.id === rfi.id ? 'rotate-90' : ''}`} />
                          </div>
                        </div>
                        
                        {/* Expanded Detail */}
                        {selectedRfi?.id === rfi.id && (
                          <div className="mt-4 pt-4 border-t space-y-3" onClick={(e) => e.stopPropagation()}>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-sm">
                              <div>
                                <p className="text-slate-500">Discipline</p>
                                <p className="font-medium">{rfi.discipline || '-'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Priority</p>
                                <p className="font-medium">{rfi.priority || 'Normal'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Issued By</p>
                                <p className="font-medium">{rfi.issued_by || '-'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Response Due</p>
                                <p className="font-medium">{formatDate(rfi.response_due_date)}</p>
                              </div>
                            </div>
                            {rfi.query && (
                              <div>
                                <p className="text-slate-500 text-sm font-medium">Query Details</p>
                                <p className="text-sm whitespace-pre-line">{rfi.query}</p>
                              </div>
                            )}
                            <div className="flex flex-col sm:flex-row gap-2 pt-2">
                              <Button 
                                size="sm" 
                                className="bg-slate-900 hover:bg-slate-800"
                                onClick={() => openResponseDialog('rfi', rfi)}
                              >
                                <Send className="h-4 w-4 mr-1" />
                                Respond to RFI
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => navigate(`/projects/${rfi.project_id}/rfi/${rfi.id}`)}>
                                <ExternalLink className="h-4 w-4 mr-1" />
                                View Full Details
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                    <p className="text-slate-600">No pending RFIs</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Delay Events Tab */}
          <TabsContent value="delayEvents">
            <Card>
              <CardContent className="p-0">
                {pendingItems.delayEvents?.length > 0 ? (
                  <div className="divide-y">
                    {pendingItems.delayEvents.map(event => (
                      <div 
                        key={event.id} 
                        className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${selectedDelayEvent?.id === event.id ? 'bg-slate-50 border-l-4 border-slate-900' : ''}`}
                        onClick={() => setSelectedDelayEvent(selectedDelayEvent?.id === event.id ? null : event)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-bold text-slate-900">{event.event_type || 'Delay Event'}</h3>
                              <Badge className={STATUS_COLORS[event.status] || 'bg-slate-100'}>
                                {event.status}
                              </Badge>
                              {event.days_active > 14 && (
                                <Badge className="bg-red-100 text-red-700">
                                  {event.days_active} days
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mb-2 line-clamp-2">{event.narrative}</p>
                            <div className="flex items-center gap-2 sm:gap-4 text-xs text-slate-500 flex-wrap">
                              <span><Building2 className="h-3 w-3 inline mr-1" />{event.project_name}</span>
                              <span><Calendar className="h-3 w-3 inline mr-1" />Started: {formatDate(event.start_date)}</span>
                              {event.days_claimed && (
                                <span><Clock className="h-3 w-3 inline mr-1" />Days Claimed: {event.days_claimed}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-2 sm:ml-4 shrink-0">
                            <ArrowRight className={`h-5 w-5 text-slate-400 transition-transform ${selectedDelayEvent?.id === event.id ? 'rotate-90' : ''}`} />
                          </div>
                        </div>
                        
                        {/* Expanded Detail */}
                        {selectedDelayEvent?.id === event.id && (
                          <div className="mt-4 pt-4 border-t space-y-3" onClick={(e) => e.stopPropagation()}>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-sm">
                              <div>
                                <p className="text-slate-500">Event Type</p>
                                <p className="font-medium">{event.event_type || '-'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Days Active</p>
                                <p className="font-medium">{event.days_active || 0} days</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Days Claimed</p>
                                <p className="font-medium">{event.days_claimed || '-'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Cost Claimed</p>
                                <p className="font-medium">{event.cost_claimed ? `R ${event.cost_claimed.toLocaleString()}` : '-'}</p>
                              </div>
                            </div>
                            {event.narrative && (
                              <div>
                                <p className="text-slate-500 text-sm font-medium">Event Narrative</p>
                                <p className="text-sm whitespace-pre-line">{event.narrative}</p>
                              </div>
                            )}
                            <div className="flex flex-col sm:flex-row gap-2 pt-2">
                              <Button 
                                size="sm" 
                                className="bg-slate-900 hover:bg-slate-800"
                                onClick={() => openResponseDialog('delayEvent', event)}
                              >
                                <MessageSquare className="h-4 w-4 mr-1" />
                                Add Comment / Determination
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => navigate(`/events/${event.id}`)}>
                                <ExternalLink className="h-4 w-4 mr-1" />
                                View Full Details
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                    <p className="text-slate-600">No delay events pending review</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Shop Drawings Tab */}
          <TabsContent value="shopDrawings">
            <Card>
              <CardContent className="p-0">
                {pendingItems.shopDrawings?.length > 0 ? (
                  <div className="divide-y">
                    {pendingItems.shopDrawings.map(sd => (
                      <div 
                        key={sd.id} 
                        className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${selectedShopDrawing?.id === sd.id ? 'bg-slate-50 border-l-4 border-slate-900' : ''}`}
                        onClick={() => setSelectedShopDrawing(selectedShopDrawing?.id === sd.id ? null : sd)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-bold text-slate-900">{sd.package_ref}</h3>
                              <Badge className={STATUS_COLORS[sd.status] || 'bg-slate-100'}>
                                {sd.status}
                              </Badge>
                              <Badge variant="outline">Rev {sd.cycle_number}</Badge>
                            </div>
                            <p className="text-sm text-slate-600 mb-2">{sd.title} - {sd.trade}</p>
                            <div className="flex items-center gap-2 sm:gap-4 text-xs text-slate-500 flex-wrap">
                              <span><Building2 className="h-3 w-3 inline mr-1" />{sd.project_name}</span>
                              <span><Calendar className="h-3 w-3 inline mr-1" />Submitted: {formatDate(sd.submitted_to_consultant_date)}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-2 sm:ml-4 shrink-0">
                            <ArrowRight className={`h-5 w-5 text-slate-400 transition-transform ${selectedShopDrawing?.id === sd.id ? 'rotate-90' : ''}`} />
                          </div>
                        </div>
                        
                        {/* Expanded Detail */}
                        {selectedShopDrawing?.id === sd.id && (
                          <div className="mt-4 pt-4 border-t space-y-3" onClick={(e) => e.stopPropagation()}>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-sm">
                              <div>
                                <p className="text-slate-500">Trade</p>
                                <p className="font-medium">{sd.trade || '-'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Revision</p>
                                <p className="font-medium">{sd.cycle_number || 1}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Subcontractor</p>
                                <p className="font-medium">{sd.subcontractor_name || '-'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Submitted</p>
                                <p className="font-medium">{formatDate(sd.submitted_to_consultant_date)}</p>
                              </div>
                            </div>
                            {sd.description && (
                              <div>
                                <p className="text-slate-500 text-sm font-medium">Description</p>
                                <p className="text-sm whitespace-pre-line">{sd.description}</p>
                              </div>
                            )}
                            {sd.drawings_count > 0 && (
                              <div>
                                <p className="text-slate-500 text-sm font-medium">Drawings ({sd.drawings_count})</p>
                                <p className="text-sm text-slate-600">Click "Review Drawings" to view all submitted drawings</p>
                              </div>
                            )}
                            <div className="flex flex-col sm:flex-row gap-2 pt-2">
                              <Button 
                                size="sm" 
                                className="bg-slate-900 hover:bg-slate-800"
                                onClick={() => openResponseDialog('shopDrawing', sd)}
                              >
                                <Send className="h-4 w-4 mr-1" />
                                Submit Review
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => navigate(`/shop-drawings/${sd.id}`)}>
                                <ExternalLink className="h-4 w-4 mr-1" />
                                View Full Details
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                    <p className="text-slate-600">No pending shop drawings</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Site Instructions Tab */}
          <TabsContent value="siteInstructions">
            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between pb-2 gap-3">
                <CardTitle className="text-base sm:text-lg font-bold">Site Instructions</CardTitle>
                <Button 
                  className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto"
                  onClick={() => openSiDialog()}
                  disabled={projects.length === 0}
                  data-testid="issue-si-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Issue Site Instruction
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {projects.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                    <p className="text-slate-600">You are not assigned to any projects as a consultant.</p>
                    <p className="text-sm text-slate-500 mt-2">Contact the project administrator to be added to a project's professional team.</p>
                  </div>
                ) : siteInstructions.length > 0 ? (
                  <div className="divide-y">
                    {siteInstructions.map(si => (
                      <div key={si.id} className="p-4 hover:bg-slate-50">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="font-bold text-slate-900">{si.si_number}</h3>
                              <Badge className={
                                si.status === 'Issued' ? 'bg-green-100 text-green-700' :
                                si.status === 'Draft' ? 'bg-slate-100 text-slate-700' :
                                si.status === 'Closed' ? 'bg-blue-100 text-blue-700' :
                                'bg-amber-100 text-amber-700'
                              }>
                                {si.status}
                              </Badge>
                              <Badge variant="outline">{si.discipline}</Badge>
                              {si.priority === 'urgent' && (
                                <Badge className="bg-red-100 text-red-700">Urgent</Badge>
                              )}
                            </div>
                            <p className="font-medium text-slate-900">{si.si_title}</p>
                            <p className="text-sm text-slate-600 mb-2">{si.si_description?.substring(0, 150)}...</p>
                            <div className="flex items-center gap-2 sm:gap-4 text-xs text-slate-500 flex-wrap">
                              <span><Building2 className="h-3 w-3 inline mr-1" />{si.project_name}</span>
                              <span><Calendar className="h-3 w-3 inline mr-1" />Issued: {formatDate(si.issue_date)}</span>
                              <span>To: {si.issued_to}</span>
                              {si.cost_impact_expected === 'Yes' && (
                                <Badge className="bg-orange-100 text-orange-700 text-xs">Cost Impact</Badge>
                              )}
                              {si.time_impact_expected === 'Yes' && (
                                <Badge className="bg-purple-100 text-purple-700 text-xs">Time Impact</Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 ml-0 sm:ml-4 shrink-0">
                            <Button variant="outline" size="sm" onClick={() => navigate(`/projects/${si.project_id}/si/${si.id}`)}>
                              <ExternalLink className="h-4 w-4 sm:mr-1" />
                              <span className="hidden sm:inline">View</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">No site instructions issued yet</p>
                    <Button 
                      className="mt-4 bg-slate-900 hover:bg-slate-800"
                      onClick={() => openSiDialog()}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Issue Your First SI
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Response Dialog */}
      <Dialog open={responseDialog.open} onOpenChange={(open) => setResponseDialog({ ...responseDialog, open })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-slate-600" />
              Submit Response
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {responseDialog.item && (
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm font-medium">
                  {responseDialog.item.reference || responseDialog.item.rfi_number || responseDialog.item.package_ref || responseDialog.item.event_type || 'Item'}
                </p>
                <p className="text-xs text-slate-500">{responseDialog.item.project_name}</p>
                {responseDialog.type === 'delayEvent' && responseDialog.item.narrative && (
                  <p className="text-xs text-slate-600 mt-2 line-clamp-2">{responseDialog.item.narrative}</p>
                )}
              </div>
            )}
            
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Decision *</Label>
              <Select value={responseForm.decision} onValueChange={(v) => setResponseForm({ ...responseForm, decision: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select decision" />
                </SelectTrigger>
                <SelectContent>
                  {responseDialog.type === 'notice' && (
                    <>
                      <SelectItem value="acknowledged">Acknowledged</SelectItem>
                      <SelectItem value="requires_info">Requires More Information</SelectItem>
                      <SelectItem value="disputed">Disputed</SelectItem>
                    </>
                  )}
                  {responseDialog.type === 'claim' && (
                    <>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="partially_approved">Partially Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="requires_info">Requires More Information</SelectItem>
                    </>
                  )}
                  {responseDialog.type === 'rfi' && (
                    <>
                      <SelectItem value="answered">Answered</SelectItem>
                      <SelectItem value="requires_clarification">Requires Clarification</SelectItem>
                      <SelectItem value="referred">Referred to Others</SelectItem>
                    </>
                  )}
                  {responseDialog.type === 'shopDrawing' && (
                    <>
                      <SelectItem value="Approved">Approved</SelectItem>
                      <SelectItem value="Approved with Comments">Approved with Comments</SelectItem>
                      <SelectItem value="Rejected">Rejected - Resubmit</SelectItem>
                    </>
                  )}
                  {responseDialog.type === 'delayEvent' && (
                    <>
                      <SelectItem value="comment">Add Comment</SelectItem>
                      <SelectItem value="approve">Approve Extension of Time</SelectItem>
                      <SelectItem value="partial">Partially Approve</SelectItem>
                      <SelectItem value="reject">Reject Extension of Time</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Comments</Label>
              <Textarea
                value={responseForm.comments}
                onChange={(e) => setResponseForm({ ...responseForm, comments: e.target.value })}
                placeholder="Enter your comments or response details..."
                className="mt-1 h-32"
              />
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setResponseDialog({ open: false, type: null, item: null })}>
              Cancel
            </Button>
            <Button onClick={handleSubmitResponse} disabled={submitting || !responseForm.decision}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Submit Response
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Site Instruction Dialog */}
      <Dialog open={siDialogOpen} onOpenChange={setSiDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Issue Site Instruction
              {nextSiNumber && (
                <Badge className="bg-slate-900 text-white ml-2">{nextSiNumber}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Project Selection */}
            <div>
              <Label className="text-sm font-medium">Project *</Label>
              <Select value={siForm.project_id} onValueChange={handleProjectChange}>
                <SelectTrigger className="mt-1" data-testid="si-project-select">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div>
              <Label className="text-sm font-medium">SI Title *</Label>
              <Input 
                className="mt-1"
                placeholder="Enter site instruction title"
                value={siForm.si_title}
                onChange={(e) => setSiForm(prev => ({ ...prev, si_title: e.target.value }))}
                data-testid="si-title-input"
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-sm font-medium">Description / Instruction *</Label>
              <Textarea 
                className="mt-1 min-h-[120px]"
                placeholder="Enter the detailed site instruction..."
                value={siForm.si_description}
                onChange={(e) => setSiForm(prev => ({ ...prev, si_description: e.target.value }))}
                data-testid="si-description-input"
              />
            </div>

            {/* Row: Discipline & Location */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Discipline</Label>
                <Select 
                  value={siForm.discipline} 
                  onValueChange={(v) => setSiForm(prev => ({ ...prev, discipline: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['Architectural', 'Structural', 'Electrical', 'Mechanical', 'Wet Services', 
                      'Fire', 'Lifts', 'Civil', 'Interior', 'Landscaping', 'General', 'Other'].map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Location / Area</Label>
                <Input 
                  className="mt-1"
                  placeholder="e.g., Level 3, Block A"
                  value={siForm.location_area}
                  onChange={(e) => setSiForm(prev => ({ ...prev, location_area: e.target.value }))}
                />
              </div>
            </div>

            {/* Row: Priority & Issued To */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Priority</Label>
                <Select 
                  value={siForm.priority} 
                  onValueChange={(v) => setSiForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Issued To</Label>
                <Input 
                  className="mt-1"
                  placeholder="Main Contractor"
                  value={siForm.issued_to}
                  onChange={(e) => setSiForm(prev => ({ ...prev, issued_to: e.target.value }))}
                />
              </div>
            </div>

            {/* Row: Cost Impact & Time Impact */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Cost Impact Expected?</Label>
                <Select 
                  value={siForm.cost_impact_expected} 
                  onValueChange={(v) => setSiForm(prev => ({ ...prev, cost_impact_expected: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Time Impact Expected?</Label>
                <Select 
                  value={siForm.time_impact_expected} 
                  onValueChange={(v) => setSiForm(prev => ({ ...prev, time_impact_expected: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Drawings Referenced */}
            <div>
              <Label className="text-sm font-medium">Drawings Referenced (comma separated)</Label>
              <Input 
                className="mt-1"
                placeholder="e.g., A-101, S-200, M-301"
                value={siForm.drawings_referenced}
                onChange={(e) => setSiForm(prev => ({ ...prev, drawings_referenced: e.target.value }))}
              />
            </div>

            {/* Affected Work Packages */}
            <div>
              <Label className="text-sm font-medium">Affected Work Packages (comma separated)</Label>
              <Input 
                className="mt-1"
                placeholder="e.g., Finishes, MEP, Structure"
                value={siForm.affected_work_packages}
                onChange={(e) => setSiForm(prev => ({ ...prev, affected_work_packages: e.target.value }))}
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
              <p className="text-sm text-amber-800">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                Upon submission, the contractor will receive an email notification and this SI will appear in their dashboard.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSiDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSiSubmit} 
              disabled={siSubmitting || !siForm.project_id || !siForm.si_title || !siForm.si_description}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="submit-si-btn"
            >
              {siSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Issue Site Instruction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-slate-900 text-white mt-12">
        <div className="max-w-7xl mx-auto px-4 py-6 text-center text-sm text-slate-400">
          <p>Consultant Portal - EOT Claims Hub</p>
        </div>
      </footer>
    </div>
  );
};

export default ConsultantPortal;
